<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcAttachmentsList',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    parent_id: {
      type: String,
      required: false,
    },
    parent: {
      type: Object,
      required: false,
    },
    readonly: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const container = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    container: container.value,
    ux: ux.value,
  }));

  const $$load_container = () => {
    return $fn.value.fetch('list_attachment', {
      parameters: { 'owner-id': props.parent_id || props.parent.id },
      asResourceContainer: true,
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [{ name: 'container' }, { name: 'ux' }],
    loaders: { container: $$load_container },
    ux: {},
    dataRefs: { container, ux },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const items = computed(() => {
    if (loaded.value) {
      return container.value.data;
    }
    return null;
  });
  const isReadOnly = computed(() => {
    return props.readonly || (props.parent && props.parent.isReadOnly);
  });

  watch(
    () => props.parent_id,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('parent_id');
    }
  );
  watch(
    () => props.parent,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('parent');
    }
  );
  watch(
    () => props.readonly,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('readonly');
    }
  );

  const act_2095089031469157_showModal_oxpwbev47_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'WAttachmentModal',
      props: {
        object_id: props.parent_id || props.parent.id,
      },
    };
  };
  const act_8513850661379123_showModal_l744sp8tr_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'WAttachmentModal',
      props: {
        object_id: props.parent_id || props.parent.id,
        attachment: _.cloneDeep($event.scope.item),
      },
    };
  };
  const act_3861238174616472_request_4b62qi8pi_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'delete_attachment',
      data: $event.scope.item,
    };
  };
  const act_3861238174616472_request_4b62qi8pi_getConfirm = async ($event) => {
    return {
      message: $fn.value.gettext('Please confirm delete.'),
      btnOk: $fn.value.gettext('Delete'),
    };
  };

  const act_7315092382398562_reloadComponentData_zlej9oey7 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_vi5sx0p4g =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_zlej9oey7,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_vi5sx0p4g],
    event: 'reload',
  });
  const act_2095089031469157_reloadComponentData_wcja6koqw =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_2095089031469157_close_wyrxtwud4 =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_reloadComponentData_wcja6koqw,
      event: 'close',
      displayName: 'reloadComponentData',
    });
  const act_2095089031469157_showModal_oxpwbev47 =
    new actionTypes.ShowModalAction({
      actionArgs: act_2095089031469157_showModal_oxpwbev47_getActionArgs,
      events: [inst_evh_2095089031469157_close_wyrxtwud4],
    });
  const inst_evh_2095089031469157_clickPreventStop_7l3df5aap =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_showModal_oxpwbev47,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_2095089031469157_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2095089031469157_clickPreventStop_7l3df5aap],
      event: 'click.prevent.stop',
    });
  const act_8513850661379123_reloadComponentData_edc9ery30 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_8513850661379123_close_1ztonejin =
    new actionTypes.EventHandlerImpl({
      action: act_8513850661379123_reloadComponentData_edc9ery30,
      event: 'close',
      displayName: 'reloadComponentData',
    });
  const act_8513850661379123_showModal_l744sp8tr =
    new actionTypes.ShowModalAction({
      actionArgs: act_8513850661379123_showModal_l744sp8tr_getActionArgs,
      events: [inst_evh_8513850661379123_close_1ztonejin],
    });
  const inst_evh_8513850661379123_clickPreventStop_udaph466p =
    new actionTypes.EventHandlerImpl({
      action: act_8513850661379123_showModal_l744sp8tr,
      event: 'click.prevent.stop',
      displayName: 'showModal',
    });
  const inst_evh_8513850661379123_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8513850661379123_clickPreventStop_udaph466p],
      event: 'click.prevent.stop',
    });
  const act_3861238174616472_reloadComponentData_8owi7ttgm =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_3861238174616472_success_2lilvsxb6 =
    new actionTypes.EventHandlerImpl({
      action: act_3861238174616472_reloadComponentData_8owi7ttgm,
      event: 'success',
      displayName: 'reloadComponentData',
    });
  const act_3861238174616472_request_4b62qi8pi = new actionTypes.RequestAction({
    actionArgs: act_3861238174616472_request_4b62qi8pi_getActionArgs,
    confirm: act_3861238174616472_request_4b62qi8pi_getConfirm,
    events: [inst_evh_3861238174616472_success_2lilvsxb6],
  });
  const inst_evh_3861238174616472_clickPreventStop_vyvbyvgm1 =
    new actionTypes.EventHandlerImpl({
      action: act_3861238174616472_request_4b62qi8pi,
      event: 'click.prevent.stop',
      displayName: 'request',
    });
  const inst_evh_3861238174616472_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_3861238174616472_clickPreventStop_vyvbyvgm1],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_reload_vi5sx0p4g,
    inst_evh_7315092382398562_reload,
    inst_evh_2095089031469157_close_wyrxtwud4,
    inst_evh_2095089031469157_clickPreventStop_7l3df5aap,
    inst_evh_2095089031469157_clickPreventStop,
    inst_evh_8513850661379123_close_1ztonejin,
    inst_evh_8513850661379123_clickPreventStop_udaph466p,
    inst_evh_8513850661379123_clickPreventStop,
    inst_evh_3861238174616472_success_2lilvsxb6,
    inst_evh_3861238174616472_clickPreventStop_vyvbyvgm1,
    inst_evh_3861238174616472_clickPreventStop,
  };

  const evh_7315092382398562_reload_vi5sx0p4g = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_vi5sx0p4g.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_close_wyrxtwud4 = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_close_wyrxtwud4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop_7l3df5aap = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop_7l3df5aap.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8513850661379123_close_1ztonejin = async (event, scope) => {
    return await actions.inst_evh_8513850661379123_close_1ztonejin.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8513850661379123_clickPreventStop_udaph466p = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_8513850661379123_clickPreventStop_udaph466p.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8513850661379123_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_8513850661379123_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3861238174616472_success_2lilvsxb6 = async (event, scope) => {
    return await actions.inst_evh_3861238174616472_success_2lilvsxb6.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3861238174616472_clickPreventStop_vyvbyvgm1 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_3861238174616472_clickPreventStop_vyvbyvgm1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3861238174616472_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_3861238174616472_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <ItpWidgetCard>
      <template #header>
        <ItpBox alignItems="center">
          <strong>
            {{ gettext('Attachments') }}
          </strong>
          <ItpButton
            :text="pgettext('Attachments', 'Upload file...')"
            variant="light"
            size="sm"
            class="ms-auto"
            v-if="!isReadOnly"
            @click.prevent.stop="
              evh_2095089031469157_clickPreventStop($event, {})
            "
          >
          </ItpButton>
        </ItpBox>
      </template>
      <BListGroup>
        <BListGroupItem v-if="!items || !items.length">
          <span>
            {{ gettext('No attachments have been added yet.') }}
          </span>
        </BListGroupItem>
        <BListGroupItem
          :key="item.id"
          class="d-flex flex-column"
          v-for="item in _.orderBy(items, ['created_at'], ['desc'])"
        >
          <ItpBox direction="row" spacing="pb-1">
            <div>
              <UcFileIcon :filename="item.file_name"> </UcFileIcon>
            </div>
            <ItpBox spacing="ps-3 w-100" direction="column">
              <ItpBox spacing="pb-1">
                <ItpLink
                  :text="item.file_name"
                  :href="item.download_url"
                  :tooltip="item.file_name"
                  :downloadFileName="item.file_name"
                  download-link-only
                  use-auth
                >
                </ItpLink>
                <ItpText class="px-3">
                  {{ filesize(item.file_size) }}
                </ItpText>
                <BDropdown
                  size="sm"
                  toggle-class="btn-default-borderless"
                  no-caret
                  variant="none"
                  class="ms-auto mb-auto"
                  v-if="!item.locked"
                >
                  <template #button-content>
                    <ItpIcon fa="ellipsis-v" class="mx-1"> </ItpIcon>
                  </template>
                  <BDropdownItem
                    @click.prevent.stop="
                      evh_8513850661379123_clickPreventStop($event, { item })
                    "
                  >
                    <ItpText :text="pgettext('Command', 'Edit')"> </ItpText>
                  </BDropdownItem>
                  <BDropdownItem
                    @click.prevent.stop="
                      evh_3861238174616472_clickPreventStop($event, { item })
                    "
                  >
                    <ItpText :text="pgettext('Command', 'Delete')"> </ItpText>
                  </BDropdownItem>
                </BDropdown>
              </ItpBox>
              <ItpBox spacing="pb-2" v-if="item.description">
                <ItpText pre :text="item.description"> </ItpText>
              </ItpBox>
              <ItpBox alignItems="center">
                <small class="text-muted text-nowrap">
                  {{ datetime(item.created_at) }}
                </small>
              </ItpBox>
            </ItpBox>
          </ItpBox>
        </BListGroupItem>
      </BListGroup>
    </ItpWidgetCard>
  </ItpUsercontrol>
</template>
